import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import {
  logoBooker, logoSimplificando,
  logoNorthsails, logoDatoquedato, logoCasablanca, logoGrim,
  logoGanaval,
} from "../assets/logos";

function WeatherSpotLogo() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="wsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0EA5E9" />
          <stop offset="100%" stopColor="#0369A1" />
        </linearGradient>
      </defs>
      <rect width="80" height="80" rx="16" fill="url(#wsGrad)" />
      <text x="40" y="22" textAnchor="middle" fill="white" fontSize="7" fontWeight="700" fontFamily="sans-serif" letterSpacing="0.5">WEATHER</text>
      <text x="40" y="33" textAnchor="middle" fill="white" fontSize="7" fontWeight="700" fontFamily="sans-serif" letterSpacing="0.5">SPOT PRO</text>
      <line x1="18" y1="44" x2="46" y2="44" stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"/>
      <line x1="18" y1="52" x2="52" y2="52" stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity="0.7"/>
      <line x1="18" y1="60" x2="40" y2="60" stroke="white" strokeWidth="2.5" strokeLinecap="round" opacity="0.5"/>
      <polyline points="42,40 50,44 42,48" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>
    </svg>
  );
}

function SecureTrackLogo() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="stpGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1E3A5F" />
          <stop offset="100%" stopColor="#0F172A" />
        </linearGradient>
      </defs>
      <rect width="80" height="80" rx="16" fill="url(#stpGrad)" />
      {/* Shield */}
      <path d="M40 14 L60 21 V40 C60 52 51 61 40 66 C29 61 20 52 20 40 V21 Z"
            fill="none" stroke="#38BDF8" strokeWidth="2.2" opacity="0.95"/>
      {/* Fingerprint arcs */}
      <path d="M33 42 C33 37 36 34 40 34 C44 34 47 37 47 42" fill="none" stroke="#38BDF8" strokeWidth="1.8" strokeLinecap="round"/>
      <path d="M29 45 C29 36 34 30 40 30 C46 30 51 36 51 45" fill="none" stroke="#38BDF8" strokeWidth="1.5" strokeLinecap="round" opacity="0.65"/>
      <path d="M37 48 C37 42 38 39 40 39 C42 39 43 42 43 48" fill="none" stroke="#38BDF8" strokeWidth="1.8" strokeLinecap="round"/>
      <text x="40" y="76" textAnchor="middle" fill="#94A3B8" fontSize="6" fontWeight="700" fontFamily="sans-serif" letterSpacing="1.2">SECURETRACK</text>
    </svg>
  );
}

function MJFLogo() {
  return (
    <svg viewBox="0 0 80 80" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="mjfGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#1a1a2e" />
          <stop offset="100%" stopColor="#4a3728" />
        </linearGradient>
      </defs>
      <rect width="80" height="80" rx="16" fill="url(#mjfGrad)" />
      <text x="40" y="34" textAnchor="middle" fill="#C9A84C" fontSize="20" fontWeight="800" fontFamily="Georgia, serif">MJF</text>
      <line x1="16" y1="42" x2="64" y2="42" stroke="#C9A84C" strokeWidth="0.8" opacity="0.6"/>
      <text x="40" y="54" textAnchor="middle" fill="#C9A84C" fontSize="6.5" fontFamily="Georgia, serif" letterSpacing="1.5" opacity="0.9">ADVISORS</text>
    </svg>
  );
}

type LogoConfig =
  | { type: "svg"; Component: React.FC }
  | { type: "img"; src: string; alt: string; bg?: string };

type ProjectDef = {
  logo: LogoConfig;
  titleKey: string;
  clientType: "own" | "client";
  subtitleKey: string;
  descKey: string;
  tags: string[];
  link?: string;
  color: string;
  textColor: string;
};

const projectDefs: ProjectDef[] = [
  {
    logo: { type: "img", src: logoBooker, alt: "BookerClub", bg: "bg-black" },
    titleKey: "BookerClub",
    clientType: "own",
    subtitleKey: "projects.booker.subtitle",
    descKey: "projects.booker.desc",
    tags: ["React", "Next.js", "Supabase", "PWA", "Multi-tenant"],
    link: "https://bookerpromanager.com",
    color: "from-sky-500/10 to-sky-600/5",
    textColor: "text-sky-600",
  },

  {
    logo: { type: "img", src: logoGanaval, alt: "Ganaval", bg: "bg-white" },
    titleKey: "Ganaval",
    clientType: "own",
    subtitleKey: "projects.ganaval.subtitle",
    descKey: "projects.ganaval.desc",
    tags: ["IoT", "LoRaWAN", "Agro", "Trazabilidad", "React"],
    link: "https://ganaval.netlify.app",
    color: "from-green-600/10 to-emerald-700/5",
    textColor: "text-green-700",
  },
  {
    logo: { type: "svg", Component: SecureTrackLogo },
    titleKey: "SecureTrack Pro",
    clientType: "own",
    subtitleKey: "projects.securetrack.subtitle",
    descKey: "projects.securetrack.desc",
    tags: ["Next.js", "Supabase", "Biometría", "GPS", "Cooperativas"],
    color: "from-slate-600/10 to-sky-700/5",
    textColor: "text-slate-700",
  },
  {
    logo: { type: "img", src: logoSimplificando, alt: "Simplificando", bg: "bg-black" },
    titleKey: "Simplificando",
    clientType: "own",
    subtitleKey: "projects.simplificando.subtitle",
    descKey: "projects.simplificando.desc",
    tags: ["PWA", "MercadoPago", "Next.js", "Argentina", "Legal"],
    link: "https://simplificando.app",
    color: "from-emerald-500/10 to-emerald-600/5",
    textColor: "text-emerald-600",
  },
  {
    logo: { type: "img", src: logoNorthsails, alt: "North Sails · Santa Tabla", bg: "bg-white" },
    titleKey: "Santa Tabla · North Sails LATAM",
    clientType: "client",
    subtitleKey: "projects.santatabla.subtitle",
    descKey: "projects.santatabla.desc",
    tags: ["E-commerce", "IA", "Automatización", "LATAM"],
    color: "from-blue-500/10 to-blue-600/5",
    textColor: "text-blue-600",
  },
  {
    logo: { type: "img", src: logoDatoquedato, alt: "Dato que Dato", bg: "bg-yellow-400" },
    titleKey: "Dato que Dato",
    clientType: "client",
    subtitleKey: "projects.datoquedato.subtitle",
    descKey: "projects.datoquedato.desc",
    tags: ["Marketplace", "Geolocalización", "IA", "Reputación"],
    link: "https://datoqdato.com",
    color: "from-yellow-400/10 to-yellow-500/5",
    textColor: "text-yellow-600",
  },
  {
    logo: { type: "img", src: logoCasablanca, alt: "CasaBlanca Polo", bg: "bg-black" },
    titleKey: "CasaBlanca Polo",
    clientType: "client",
    subtitleKey: "projects.casablanca.subtitle",
    descKey: "projects.casablanca.desc",
    tags: ["PWA", "Carbon Fiber", "SVG", "Polo", "High Performance"],
    link: "https://casablancapolo.com",
    color: "from-pink-500/10 to-orange-400/5",
    textColor: "text-pink-600",
  },
  {
    logo: { type: "svg", Component: WeatherSpotLogo },
    titleKey: "WeatherSpot Pro",
    clientType: "own",
    subtitleKey: "projects.weatherspot.subtitle",
    descKey: "projects.weatherspot.desc",
    tags: ["PWA", "Windguru API", "Water Sports", "White-label", "React"],
    link: "https://weatherspotpro.netlify.app",
    color: "from-sky-400/10 to-cyan-500/5",
    textColor: "text-sky-600",
  },
  {
    logo: { type: "svg", Component: MJFLogo },
    titleKey: "MJF Furtado Advisors",
    clientType: "client",
    subtitleKey: "projects.mjf.subtitle",
    descKey: "projects.mjf.desc",
    tags: ["Real Estate", "Landing Page", "React", "Bilingüe"],
    link: "https://www.mjfurtado.com",
    color: "from-amber-800/10 to-yellow-700/5",
    textColor: "text-amber-800",
  },
  {
    logo: { type: "img", src: logoGrim, alt: "GRIM Execution Sheet", bg: "bg-black" },
    titleKey: "GRIM Execution Sheet",
    clientType: "own",
    subtitleKey: "projects.grim.subtitle",
    descKey: "projects.grim.desc",
    tags: ["PWA", "Productividad", "Bolt.new", "React", "The GRIM Circle"],
    link: "https://grimcircles.com",
    color: "from-yellow-500/10 to-amber-600/5",
    textColor: "text-amber-600",
  },
];

function ProjectLogo({ logo }: { logo: LogoConfig }) {
  if (logo.type === "svg") return <logo.Component />;
  return (
    <div className={`w-full h-full ${logo.bg ?? "bg-white"} flex items-center justify-center p-1.5`}>
      <img src={logo.src} alt={logo.alt} className="w-full h-full object-contain" />
    </div>
  );
}

export default function ProjectsSection() {
  const { t } = useLanguage();
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-primary/10 text-primary font-semibold text-sm px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
            {t('projects.badge')}
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('projects.title')}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('projects.description')}</p>
        </div>

        {/* Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projectDefs.map((p, i) => (
            <div
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`
                relative bg-gradient-to-br ${p.color}
                border border-gray-100 rounded-2xl p-6
                shadow-sm hover:shadow-xl transition-all duration-300
                ${hovered === i ? "scale-[1.02] border-gray-200" : "scale-100"}
              `}
            >
              {/* Badge */}
              <span className={`
                absolute top-4 right-4 text-xs font-medium px-2.5 py-1 rounded-full
                ${p.clientType === "own" ? "bg-primary/10 text-primary" : "bg-gray-100 text-gray-500"}
              `}>
                {p.clientType === "own" ? t('projects.ownProduct') : t('projects.client')}
              </span>

              {/* Logo */}
              <div className="w-16 h-16 mb-4 rounded-xl overflow-hidden shadow-md">
                <ProjectLogo logo={p.logo} />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-0.5 pr-20">{p.titleKey}</h3>
              <p className={`text-sm font-semibold mb-3 ${p.textColor}`}>{t(p.subtitleKey)}</p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{t(p.descKey)}</p>

              {/* Tags */}
              <div className="flex flex-wrap gap-1.5">
                {p.tags.map((tag, j) => (
                  <span key={j} className="text-xs bg-white/80 border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              {p.link && (
                <a href={p.link} target="_blank" rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 mt-4 text-sm font-medium ${p.textColor} hover:underline`}>
                  {t('projects.visitSite')}
                </a>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-14">
          <p className="text-gray-600 mb-4 text-lg">{t('projects.ctaQuestion')}</p>
          <a href="#get-started"
            className="inline-block bg-primary hover:bg-primary-dark text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md">
            {t('projects.ctaButton')}
          </a>
        </div>
      </div>
    </section>
  );
}


